import {
  DefaultDevicesSettingsDTO,
  LightsActuatorSettingsObjectDTO,
  LightsCommandSettingsObjectDTO,
  LightsOnOffMode,
  LockActuatorSettingsObjectParam,
  LockCommandSettingsObjectDTO,
  ShuttersActuatorSettingsObjectParam,
  ShuttersCommandSettingsObjectDTO,
  ThermoProbeSettingsObjectParam,
} from '@/../client-generator/generated-clients/device-settings';
import {
  CommandIcon,
  DeviceSettingsDTO,
  IDeviceSettingsDTO,
  ILightsActuatorSettingsResult,
  ILightsActuatorSettingsResultList,
  ILightsCommandSettingsResult,
  ILightsCommandSettingsResultList,
  ILockActuatorSettingsResult,
  ILockCommandSettingsResult,
  ILockCommandSettingsResultList,
  IShutterActuatorSettingsResult,
  IShutterCommandSettingsResult,
  IShutterCommandSettingsResultList,
  IThermoProbeSettingsResult,
  LightsActuatorSettingsResult,
  LightsActuatorSettingsResultList,
  LightsCommandSettingsResult,
  LightsCommandSettingsResultList,
  LockActuatorSettingsResult,
  LockCommandSettingsResult,
  LockCommandSettingsResultList,
  ShutterActuatorSettingsResult,
  ShutterCommandSettingsResult,
  ShutterCommandSettingsResultList,
  ThermoProbeSettingsResult,
} from '@/../client-generator/generated-clients/plants';

export default class DeviceSettingsService {
  static generateDeviceSettingsResultDto(
    deviceSettings: DefaultDevicesSettingsDTO[]
  ) {
    const settingsResultList: DeviceSettingsDTO[] = [];

    for (const setting of deviceSettings) {
      if (setting.isAggregateRoomObject) continue;
      if (!setting.deviceUid) return;
      let settingResult: IDeviceSettingsDTO = {
        deviceId: setting.deviceUid,
        commandIcon: setting.selectedCommandIcon,
      };

      if (setting.lightsActuatorSettings != null)
        settingResult = this.transformLightsActuatorSettings(
          setting.lightsActuatorSettings,
          setting.deviceUid
        );
      else if (setting.lightsCommandSettings != null)
        settingResult = this.transformLightsCommandSettings(
          setting.lightsCommandSettings,
          setting.deviceUid,
          setting.selectedCommandIcon
        );
      else if (setting.lockActuatorSettings != null)
        settingResult = this.transformLockActuatorSettings(
          setting.lockActuatorSettings,
          setting.deviceUid
        );
      else if (setting.lockCommandSettings != null)
        settingResult = this.transformLockCommandSettings(
          setting.lockCommandSettings,
          setting.deviceUid,
          setting.selectedCommandIcon
        );
      else if (setting.thermoProbeSettings != null)
        settingResult = this.transformThermoProbeSettings(
          setting.thermoProbeSettings,
          setting.deviceUid
        );
      else if (setting.shuttersActuatorSettings != null)
        settingResult = this.transformShuttersActuatorSettings(
          setting.shuttersActuatorSettings,
          setting.deviceUid
        );
      else if (setting.shuttersCommandSettings != null)
        settingResult = this.transformShuttersCommandSettings(
          setting.shuttersCommandSettings,
          setting.deviceUid,
          setting.selectedCommandIcon
        );

      settingsResultList.push(new DeviceSettingsDTO(settingResult));
    }

    return settingsResultList;
  }

  //  Xamarin --> LightsActuatorSettingsViewModel.cs | DoConfirmCommand()
  private static transformLightsActuatorSettings(
    settings: LightsActuatorSettingsObjectDTO,
    deviceId: string
  ) {
    const results: LightsActuatorSettingsResult[] = [];
    for (const slot of settings.slots!) {
      for (const index of slot.saveTo!) {
        results.push(
          new LightsActuatorSettingsResult({
            index,
            hasPul: slot.hasPUL,
            isMinimumregulationLevelEnabled:
              slot.isMinimumregulationLevelEnabled,
            loadType: slot.loadType,
            minimumRegulationLevelValue: slot.minimumRegulationLevelValue,
            voltageType: slot.voltageType,
            isRestoreStateAfterResetEnabled:
              slot.isRestoreStateAfterResetEnabled,
            isZeroCrossingEnabled: slot.isZeroCrossingEnabled,
            relayStatusAfterReset: slot.relayStatusAfterReset,
          } as ILightsActuatorSettingsResult)
        );
      }
    }

    const result: IDeviceSettingsDTO = {
      lightsActuatorSettings: new LightsActuatorSettingsResultList({
        results,
      } as ILightsActuatorSettingsResultList),
      deviceId,
    };
    return result;
  }

  //  Xamarin --> LightsCommandSettingsViewModel.cs | DoConfirmCommand()
  private static transformLightsCommandSettings(
    settings: LightsCommandSettingsObjectDTO,
    deviceId: string,
    commandIcon?: CommandIcon
  ) {
    const results: LightsCommandSettingsResult[] = [];
    for (const slot of settings.slots!) {
      for (const index of slot.saveTo!) {
        results.push(
          new LightsCommandSettingsResult({
            index,
            lightsOnOffMode: slot.lightsOnOffMode,
            lightsOnOffTimedDelay:
              slot.lightsOnOffMode == LightsOnOffMode.Timed
                ? slot.timedDelayPickerSelectedValue!.value
                : slot.defaultSettings!.lightsOnOffTimedDelay,
            minimumRegulationLevel: slot.minimumRegulationLevel,
          } as ILightsCommandSettingsResult)
        );
      }
    }

    const result: IDeviceSettingsDTO = {
      lightsCommandSettings: new LightsCommandSettingsResultList({
        results,
      } as ILightsCommandSettingsResultList),
      deviceId,
      commandIcon,
    };
    return result;
  }

  //  Xamarin --> LockActuatorSettingsViewModel.cs | DoConfirmCommand()
  private static transformLockActuatorSettings(
    settings: LockActuatorSettingsObjectParam,
    deviceId: string
  ) {
    const result: IDeviceSettingsDTO = {
      lockActuatorSettings: new LockActuatorSettingsResult({
        isZeroCrossingEnabled: settings.isZeroCrossingEnabled,
      } as ILockActuatorSettingsResult),
      deviceId,
    };
    return result;
  }

  //  Xamarin --> LockCommandSettingsViewModel.cs | DoConfirmCommand()
  private static transformLockCommandSettings(
    settings: LockCommandSettingsObjectDTO,
    deviceId: string,
    commandIcon?: CommandIcon
  ) {
    const results: LockCommandSettingsResult[] = [];
    for (const slot of settings.slots!) {
      for (const index of slot.saveTo!) {
        results.push(
          new LockCommandSettingsResult({
            index,
            commandOpeningTime: slot.commandOpeningTime,
          } as ILockCommandSettingsResult)
        );
      }
    }

    const result: IDeviceSettingsDTO = {
      lockCommandSettingsResult: new LockCommandSettingsResultList({
        results,
      } as ILockCommandSettingsResultList),
      deviceId,
      commandIcon,
    };
    return result;
  }

  //  Xamarin --> ThermoProbeSettingsViewModel.cs | DoConfirmCommand()
  private static transformThermoProbeSettings(
    settings: ThermoProbeSettingsObjectParam,
    deviceId: string
  ) {
    const result: IDeviceSettingsDTO = {
      thermoProbeSettings: new ThermoProbeSettingsResult({
        isLedmanaged: settings.isLedmanaged,
        externalSensorType: settings.externalSensorType,
      } as IThermoProbeSettingsResult),
      deviceId,
    };
    return result;
  }

  //  Xamarin --> ShutterActuatorSettingsViewModel.cs | DoConfirmCommand()
  private static transformShuttersActuatorSettings(
    settings: ShuttersActuatorSettingsObjectParam,
    deviceId: string
  ) {
    const result: IDeviceSettingsDTO = {
      shutterActuatorSettings: new ShutterActuatorSettingsResult({
        hasPUL: settings.hasPUL,
        shutterMotorType: settings.shutterMotorType,
        stopPulseDuration: settings.stopPulseDuration! / 100,
        upDownPulseDuration: settings.upDownPulseDuration! / 100,
        shutterMode: settings.shutterMode,
        hasPreset: settings.hasPreset,
        presetValue: settings.presetValue! / 10,
        stopTime: settings.stopTime!,
        delayBetweenDoorsTime: settings.delayBetweenDoorsTime! / 1000,
      } as IShutterActuatorSettingsResult),
      deviceId,
    };
    return result;
  }

  //  Xamarin --> ShutterCommandSettingsViewModel.cs | DoConfirmCommand()
  private static transformShuttersCommandSettings(
    settings: ShuttersCommandSettingsObjectDTO,
    deviceId: string,
    commandIcon?: CommandIcon
  ) {
    const results: ShutterCommandSettingsResult[] = [];
    for (const slot of settings.slots!) {
      for (const index of slot.saveTo!) {
        results.push(
          new ShutterCommandSettingsResult({
            index,
            shutterMode: slot.shutterMode,
            presetValue: slot.presetValue,
            hasPreset: slot.hasPreset,
            commandDirection: slot.commandDirection,
          } as IShutterCommandSettingsResult)
        );
      }
    }

    const result: IDeviceSettingsDTO = {
      shutterCommandSettings: new ShutterCommandSettingsResultList({
        results,
      } as IShutterCommandSettingsResultList),
      deviceId,
      commandIcon,
    };
    return result;
  }

  // QUESTO ANDRà SPOSTATO ????
  private static CalculateStopTime(value: number) {
    let stopTimeValue: number;
    let remainder: number = value % 60000;
    let quotient: number = Math.floor(value / 60000);
    if (quotient > 0) {
      if (quotient == 1) {
        stopTimeValue = 60;
      } else {
        stopTimeValue = 60 + quotient;
      }
    } else {
      stopTimeValue = remainder / 1000;
    }

    return stopTimeValue;
  }
}
